// src/components/AdUnit.jsx
// ─────────────────────────────────────────────────────────────────────────────
// AdSense-safe ad unit.
//
// POLICY VIOLATIONS FIXED:
//
// 1. "Google-served ads on screens without publisher-content"
//    The root cause was that the old code only suppressed the ad if the
//    *current* DOM had too little text — but the landing page renders its
//    rich content AFTER ads were pushed (first-paint was sparse).
//    Fix: ads are never pushed on mount; they wait until the page has been
//    rendered for at least PAGE_SETTLE_MS (800ms), giving React time to paint
//    all content before Google crawls the ad slot.
//
// 2. "Low value content"
//    Pages like the map-only view or the prediction form have very little
//    editorial text. Fix: minContentLength raised to 400 chars and we now
//    also check for at least one semantic content element (article, section,
//    main > p) so nav/footer text alone won't unlock ads.
//
// 3. Empty ad containers occupying layout space
//    Fixed: the component returns null until the ad is actually filled.
//
// USAGE:
//   <AdUnit slot="1234567890" />                        ← auto content gate
//   <AdUnit slot="1234567890" requiresContent={false} /> ← opt-out (proven rich pages only)
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';

const PAGE_SETTLE_MS   = 800;   // wait for React to finish painting before pushing
const CONTENT_MIN_CHARS = 400;  // minimum visible editorial text on the page
const AD_FILL_TIMEOUT  = 6000;  // give up waiting for fill after 6 s

export default function AdUnit({
  slot,
  format = 'auto',
  layout,
  layoutKey,
  style = {},
  requiresContent = true,
  minContentLength = CONTENT_MIN_CHARS,
}) {
  const [filled,    setFilled]    = useState(false);
  const [contentOk, setContentOk] = useState(!requiresContent);
  const pushed = useRef(false);

  // ── Step 1: Content gate — wait for page to settle, then check ────────────
  useEffect(() => {
    if (!requiresContent) { setContentOk(true); return; }

    let timer;
    const check = () => {
      // Count visible editorial text, excluding nav/header/script/ad nodes
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode(node) {
            const el  = node.parentElement;
            const tag = el?.tagName?.toLowerCase() || '';
            if (['script', 'style', 'noscript', 'nav', 'header', 'ins'].includes(tag)) {
              return NodeFilter.FILTER_REJECT;
            }
            if (el?.closest?.('ins.adsbygoogle, nav, header, footer')) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          },
        }
      );

      let total = 0;
      let node;
      while ((node = walker.nextNode())) {
        total += (node.textContent || '').trim().length;
        if (total >= minContentLength) break;
      }

      // Also require at least one semantic content element so that pages
      // with only a map/form don't trigger ads
      const hasContentEl = Boolean(
        document.querySelector('article, [role="article"], main p, section p, .blog-content, .article-body')
      );

      setContentOk(total >= minContentLength && hasContentEl);
    };

    // Wait for React to finish rendering the page before checking
    timer = setTimeout(check, PAGE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [requiresContent, minContentLength]);

  // ── Step 2: Push the ad once content gate passes ──────────────────────────
  useEffect(() => {
    if (!contentOk || pushed.current) return;
    pushed.current = true;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (_) {
      // AdSense script not loaded (dev environment)
      return;
    }

    // Poll for fill status (data-ad-status="filled" set by AdSense)
    let attempts = 0;
    const maxAttempts = AD_FILL_TIMEOUT / 500;
    const poll = setInterval(() => {
      const ins = document.querySelector(`ins[data-ad-slot="${slot}"]`);
      if (ins?.getAttribute('data-ad-status') === 'filled') {
        setFilled(true);
        clearInterval(poll);
      }
      if (++attempts >= maxAttempts) clearInterval(poll);
    }, 500);

    return () => clearInterval(poll);
  }, [contentOk, slot]);

  // Don't render anything until the ad is confirmed filled —
  // prevents empty white boxes that also trigger "no publisher content" policy.
  if (!contentOk || !filled) return null;

  return (
    <div style={{ overflow: 'hidden' }}>
      <ins
        className="adsbygoogle"
        style={{ display: 'block', ...style }}
        data-ad-client="ca-pub-3501961170533687"
        data-ad-slot={slot}
        data-ad-format={format}
        {...(layout    && { 'data-ad-layout':     layout    })}
        {...(layoutKey && { 'data-ad-layout-key': layoutKey })}
        data-full-width-responsive="true"
      />
    </div>
  );
}